import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, X, CheckCircle2 } from 'lucide-react';

export default function AdminCourses() {
  const [tab, setTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [courseModal, setCourseModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [completionModal, setCompletionModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes, eRes, rRes, catRes] = await Promise.all([
        adminApi.getCourses(),
        adminApi.getAssignments(),
        adminApi.getEmployees({ is_active: 'True' }),
        adminApi.getRestaurants(),
        adminApi.getCategories(),
      ]);
      setCourses(cRes.data.results || cRes.data);
      setAssignments(aRes.data.results || aRes.data);
      setEmployees(eRes.data.results || eRes.data);
      setRestaurants(rRes.data.results || rRes.data);
      setCategories(catRes.data.results || catRes.data);
    } catch { toast.error('Error al cargar datos.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Course CRUD ────────────────────────
  const handleSaveCourse = async () => {
    if (!courseModal?.name) { toast.error('Introduce un nombre.'); return; }
    try {
      if (courseModal.id) {
        await adminApi.updateCourse(courseModal.id, courseModal);
      } else {
        await adminApi.createCourse(courseModal);
      }
      toast.success('Curso guardado.');
      setCourseModal(null);
      load();
    } catch { toast.error('Error al guardar.'); }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('¿Eliminar este curso?')) return;
    try {
      await adminApi.deleteCourse(id);
      toast.success('Curso eliminado.');
      load();
    } catch { toast.error('Error al eliminar.'); }
  };

  // ─── Assignment CRUD ────────────────────
  const handleSaveAssignment = async () => {
    if (!assignModal?.course || !assignModal?.deadline) {
      toast.error('Selecciona curso y fecha límite.');
      return;
    }
    try {
      const data = {
        course: assignModal.course,
        deadline: assignModal.deadline,
        employee: assignModal.employee || null,
        restaurant: assignModal.restaurant || null,
        category: assignModal.category || null,
        min_seniority_days: assignModal.min_seniority_days ? parseInt(assignModal.min_seniority_days) : null,
        max_seniority_days: assignModal.max_seniority_days ? parseInt(assignModal.max_seniority_days) : null,
      };
      if (assignModal.id) {
        await adminApi.updateAssignment(assignModal.id, data);
      } else {
        await adminApi.createAssignment(data);
      }
      toast.success('Asignación guardada.');
      setAssignModal(null);
      load();
    } catch { toast.error('Error al guardar asignación.'); }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('¿Eliminar esta asignación?')) return;
    try {
      await adminApi.deleteAssignment(id);
      toast.success('Asignación eliminada.');
      load();
    } catch { toast.error('Error al eliminar.'); }
  };

  // ─── Mark completion ────────────────────
  const handleMarkComplete = async () => {
    if (!completionModal?.employee || !completionModal?.course) {
      toast.error('Selecciona empleado y curso.');
      return;
    }
    try {
      await adminApi.createCompletion({
        employee: completionModal.employee,
        course: completionModal.course,
        assignment: completionModal.assignment || null,
      });
      toast.success('Curso marcado como completado.');
      setCompletionModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar.');
    }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="page-header">
        <h1>Gestión de Cursos</h1>
        <p>Cursos, asignaciones y seguimiento de completados</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'courses' ? 'tab--active' : ''}`} onClick={() => setTab('courses')}>
          Cursos
        </button>
        <button className={`tab ${tab === 'assignments' ? 'tab--active' : ''}`} onClick={() => setTab('assignments')}>
          Asignaciones
        </button>
      </div>

      {/* ═══ COURSES TAB ═══ */}
      {tab === 'courses' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn--primary" onClick={() => setCourseModal({ name: '', description: '', url: '', is_mandatory: false })}>
              <Plus size={16} /> Nuevo curso
            </button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Enlace</th>
                  <th>Obligatorio</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {courses.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td style={{ fontSize: '0.84rem', color: 'var(--gray-500)', maxWidth: 250 }}>{c.description || '—'}</td>
                    <td>
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem' }}>Abrir</a>
                      ) : <span style={{ color: 'var(--gray-400)', fontSize: '0.82rem' }}>—</span>}
                    </td>
                    <td>
                      {c.is_mandatory
                        ? <span className="badge badge--danger">Obligatorio</span>
                        : <span className="badge badge--neutral">Opcional</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn--ghost btn--sm" onClick={() => setCourseModal({ ...c })}>Editar</button>
                        <button className="btn btn--ghost btn--sm" onClick={() => handleDeleteCourse(c.id)}>
                          <Trash2 size={14} color="var(--danger)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══ ASSIGNMENTS TAB ═══ */}
      {tab === 'assignments' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <button className="btn btn--secondary" onClick={() => setCompletionModal({ employee: '', course: '', assignment: '' })}>
              <CheckCircle2 size={16} /> Marcar curso completado
            </button>
            <button className="btn btn--primary" onClick={() => setAssignModal({ course: '', deadline: '', employee: '', restaurant: '', category: '', min_seniority_days: '', max_seniority_days: '' })}>
              <Plus size={16} /> Nueva asignación
            </button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Curso</th>
                  <th>Asignado a</th>
                  <th>Fecha límite</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 ? (
                  <tr><td colSpan={5} className="empty-state"><p>No hay asignaciones</p></td></tr>
                ) : assignments.map(a => {
                  const target = a.employee_name
                    ? `Empleado: ${a.employee_name}`
                    : a.restaurant_name && a.category_name
                    ? `${a.restaurant_name} + ${a.category_name}`
                    : a.restaurant_name
                    ? `Restaurante: ${a.restaurant_name}`
                    : a.category_name
                    ? `Categoría: ${a.category_name}`
                    : 'Global';
                  const isOverdue = new Date(a.deadline) < new Date() && a.status === 'pending';
                  return (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 500 }}>{a.course_name}</td>
                      <td style={{ fontSize: '0.84rem' }}>{target}</td>
                      <td>
                        <span style={{ color: isOverdue ? 'var(--danger)' : 'inherit', fontWeight: isOverdue ? 600 : 400 }}>
                          {new Date(a.deadline).toLocaleDateString('es-ES')}
                          {isOverdue && ' ⚠'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge--${a.status === 'completed' ? 'success' : isOverdue ? 'danger' : 'warning'}`}>
                          {a.status === 'completed' ? 'Completado' : isOverdue ? 'Vencido' : 'Pendiente'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn--ghost btn--sm" onClick={() => handleDeleteAssignment(a.id)}>
                          <Trash2 size={14} color="var(--danger)" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══ COURSE MODAL ═══ */}
      {courseModal && (
        <div className="modal-overlay" onClick={() => setCourseModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>{courseModal.id ? 'Editar curso' : 'Nuevo curso'}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCourseModal(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label>Nombre *</label>
              <input className="form-input" value={courseModal.name} onChange={e => setCourseModal({ ...courseModal, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <textarea className="form-input" value={courseModal.description} onChange={e => setCourseModal({ ...courseModal, description: e.target.value })} rows={3} />
            </div>
            <div className="form-group">
              <label>Enlace al curso (URL)</label>
              <input className="form-input" value={courseModal.url || ''} onChange={e => setCourseModal({ ...courseModal, url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={courseModal.is_mandatory} onChange={e => setCourseModal({ ...courseModal, is_mandatory: e.target.checked })} style={{ marginRight: 8 }} />
                Curso obligatorio
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setCourseModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleSaveCourse}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ASSIGNMENT MODAL ═══ */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Nueva asignación de curso</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setAssignModal(null)}><X size={18} /></button>
            </div>
            <div className="alert alert--info" style={{ fontSize: '0.84rem' }}>
              Puedes asignar un curso a un empleado específico, a un restaurante, a una categoría profesional o a una combinación de restaurante + categoría.
            </div>
            <div className="form-group">
              <label>Curso *</label>
              <select className="form-input" value={assignModal.course} onChange={e => setAssignModal({ ...assignModal, course: e.target.value })}>
                <option value="">Seleccionar curso</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Fecha límite *</label>
              <input className="form-input" type="date" value={assignModal.deadline} onChange={e => setAssignModal({ ...assignModal, deadline: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Empleado específico (opcional)</label>
              <select className="form-input" value={assignModal.employee} onChange={e => setAssignModal({ ...assignModal, employee: e.target.value })}>
                <option value="">Ninguno — asignar por grupo</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} ({emp.employee_code})</option>
                ))}
              </select>
            </div>
            {!assignModal.employee && (
              <>
                <div className="form-group">
                  <label>Restaurante (opcional)</label>
                  <select className="form-input" value={assignModal.restaurant} onChange={e => setAssignModal({ ...assignModal, restaurant: e.target.value })}>
                    <option value="">Todos</option>
                    {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Categoría profesional (opcional)</label>
                  <select className="form-input" value={assignModal.category} onChange={e => setAssignModal({ ...assignModal, category: e.target.value })}>
                    <option value="">Todas</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>Antigüedad mínima (días)</label>
                <input className="form-input" type="number" value={assignModal.min_seniority_days} onChange={e => setAssignModal({ ...assignModal, min_seniority_days: e.target.value })} placeholder="Ej: 365 (1 año)" />
              </div>
              <div className="form-group">
                <label>Antigüedad máxima (días)</label>
                <input className="form-input" type="number" value={assignModal.max_seniority_days} onChange={e => setAssignModal({ ...assignModal, max_seniority_days: e.target.value })} placeholder="Ej: 90 (3 meses)" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setAssignModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleSaveAssignment}>Crear asignación</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ COMPLETION MODAL ═══ */}
      {completionModal && (
        <div className="modal-overlay" onClick={() => setCompletionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Marcar curso completado</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCompletionModal(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label>Empleado *</label>
              <select className="form-input" value={completionModal.employee} onChange={e => setCompletionModal({ ...completionModal, employee: e.target.value })}>
                <option value="">Seleccionar</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} ({emp.employee_code})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Curso *</label>
              <select className="form-input" value={completionModal.course} onChange={e => setCompletionModal({ ...completionModal, course: e.target.value })}>
                <option value="">Seleccionar</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setCompletionModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleMarkComplete}>
                <CheckCircle2 size={16} /> Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
